import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Layers, CheckCircle, Clock, AlertTriangle, DollarSign,
  Loader, RefreshCw, Send, Copy, Mail,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * InstallmentManagementModal — Phase 2b admin UI for managing an
 * appointment's installment plan.
 *
 * Shows every chunk in the plan with its current status, the chunk's amount
 * and method (online | in_person), and per-pending-chunk action buttons.
 *
 * Phase 2b scope:
 *   - "Mark paid (cash)" — calls PATCH /api/appointments/:id/installments/:n/pay
 *     with payment_method_used='cash'. Use case: in-person chunk that the
 *     client has just paid in cash at the appointment, OR an online chunk
 *     that the admin took payment for outside the booking flow.
 *   - "Send payment link" — placeholder button for online chunks #2+ that
 *     are still pending. Wired in Phase 2b-next-session along with the
 *     payment-link generation backend.
 *
 * Calls onUpdate() after any successful change so the parent appointment
 * list can refresh and reflect the new payment_status / amount_paid.
 */
const InstallmentManagementModal = ({ isOpen, onClose, appointment, onUpdate }) => {
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actingOn, setActingOn] = useState(null); // installment_number currently being mutated
  const [sendingLink, setSendingLink] = useState(null); // installment_number whose link is being generated
  const [lastLinkResult, setLastLinkResult] = useState(null); // { number, url, sent_to, email_sent }

  const loadInstallments = useCallback(async () => {
    if (!appointment?.id) return;
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.get(
        `${API_URL}/appointments/${appointment.id}/installments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInstallments(res.data?.installments || []);
    } catch (err) {
      console.error('Failed to load installments:', err);
      toast.error('Failed to load installment plan');
      setInstallments([]);
    } finally {
      setLoading(false);
    }
  }, [appointment?.id]);

  useEffect(() => {
    if (isOpen && appointment?.id) {
      loadInstallments();
    }
  }, [isOpen, appointment?.id, loadInstallments]);

  if (!isOpen || !appointment) return null;

  const totalPrice = parseFloat(appointment.total_price || 0);
  const paidSum = installments
    .filter((i) => i.status === 'paid')
    .reduce((s, i) => s + parseFloat(i.amount_due || 0), 0);
  const remainingSum = installments
    .filter((i) => i.status !== 'paid')
    .reduce((s, i) => s + parseFloat(i.amount_due || 0), 0);
  const paidCount = installments.filter((i) => i.status === 'paid').length;
  const totalCount = installments.length;

  const handleSendLink = async (installmentNumber) => {
    if (!window.confirm(
      `Send a payment link for installment #${installmentNumber} to the client's email?\n\n` +
      `This generates a secure single-purpose link that expires in 7 days. Any previously-sent ` +
      `link for this installment will stop working.`
    )) return;
    setSendingLink(installmentNumber);
    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/appointments/${appointment.id}/installments/${installmentNumber}/send-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastLinkResult({
        number: installmentNumber,
        url: res.data.payment_url,
        sent_to: res.data.sent_to,
        email_sent: res.data.email_sent,
        message: res.data.message,
      });
      if (res.data.email_sent) {
        toast.success(res.data.message);
      } else {
        toast.warn(res.data.message || 'Email could not be sent. Use the link below to share manually.');
      }
      await loadInstallments();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to send installment link:', err);
      toast.error(err.response?.data?.error || 'Failed to send payment link');
    } finally {
      setSendingLink(null);
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Payment link copied to clipboard');
    } catch (err) {
      toast.error('Could not copy. Select the URL and copy it manually.');
    }
  };

  const handleMarkPaid = async (installmentNumber, methodLabel = 'cash') => {
    if (!window.confirm(`Mark installment #${installmentNumber} as paid (${methodLabel})?`)) return;
    setActingOn(installmentNumber);
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(
        `${API_URL}/appointments/${appointment.id}/installments/${installmentNumber}/pay`,
        { payment_method_used: methodLabel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Installment #${installmentNumber} marked paid`);
      await loadInstallments();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to mark installment paid:', err);
      toast.error(err.response?.data?.error || 'Failed to mark installment paid');
    } finally {
      setActingOn(null);
    }
  };

  // Visual styling per chunk status.
  const statusBadge = (chunk) => {
    if (chunk.status === 'paid') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
          <CheckCircle size={12} className="mr-1" /> Paid
        </span>
      );
    }
    if (chunk.status === 'overdue') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800">
          <AlertTriangle size={12} className="mr-1" /> Overdue
        </span>
      );
    }
    if (chunk.status === 'refunded') {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
          Refunded
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-800">
        <Clock size={12} className="mr-1" /> Pending
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-sky-50">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-sky-100">
              <Layers className="text-sky-600" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Installment Plan</h2>
              <p className="text-sm text-gray-600">
                Appointment #{appointment.id?.slice(0, 8)}
                {appointment.client_name && ` · ${appointment.client_name}`}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadInstallments}
              disabled={loading}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Refresh"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader size={20} className="animate-spin mr-2" />
              Loading plan...
            </div>
          ) : installments.length === 0 ? (
            <div className="p-6 bg-gray-50 border border-gray-200 rounded text-center text-gray-600">
              <DollarSign size={32} className="mx-auto mb-2 text-gray-400" />
              <p className="font-medium">No installment plan for this appointment.</p>
              <p className="text-sm text-gray-500 mt-1">
                Use the standard <em>Record Partial Payment</em> action to log
                ad-hoc payments instead.
              </p>
            </div>
          ) : (
            <>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="p-3 bg-gray-50 border border-gray-200 rounded">
                  <div className="text-xs text-gray-500 uppercase">Plan Total</div>
                  <div className="text-lg font-bold text-gray-900">${totalPrice.toFixed(2)}</div>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <div className="text-xs text-green-700 uppercase">Paid</div>
                  <div className="text-lg font-bold text-green-900">
                    ${paidSum.toFixed(2)}
                    <span className="text-xs font-normal ml-1">({paidCount}/{totalCount})</span>
                  </div>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <div className="text-xs text-amber-700 uppercase">Remaining</div>
                  <div className="text-lg font-bold text-amber-900">${remainingSum.toFixed(2)}</div>
                </div>
              </div>

              {/* Chunk list */}
              <div className="space-y-2">
                {installments.map((chunk) => {
                  const isPending = chunk.status === 'pending' || chunk.status === 'overdue';
                  const isOnline = chunk.method === 'online';
                  const isFirst = chunk.installment_number === 1;
                  return (
                    <div
                      key={chunk.id}
                      className={`p-3 rounded border-2 ${
                        chunk.status === 'paid'
                          ? 'border-green-200 bg-green-50'
                          : chunk.status === 'overdue'
                          ? 'border-red-200 bg-red-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-3">
                          <span className="font-semibold text-gray-900">
                            #{chunk.installment_number}
                          </span>
                          <span className="text-xl font-bold text-gray-900">
                            ${parseFloat(chunk.amount_due).toFixed(2)}
                          </span>
                          <span className="text-xs text-gray-500 uppercase">
                            {isOnline ? 'Online' : 'In person'}
                          </span>
                          {statusBadge(chunk)}
                        </div>
                        {isPending && (
                          <div className="flex items-center space-x-2">
                            {/* Send Link: only for online chunks #2+. Chunk #1
                                is paid at booking via the booking flow itself. */}
                            {isOnline && !isFirst && (
                              <button
                                onClick={() => handleSendLink(chunk.installment_number)}
                                disabled={sendingLink === chunk.installment_number}
                                className="px-3 py-1 text-sm bg-sky-600 text-white rounded hover:bg-sky-700 disabled:opacity-50 inline-flex items-center"
                                title="Email the client a single-purpose payment link for this chunk"
                              >
                                {sendingLink === chunk.installment_number ? (
                                  <><Loader size={12} className="animate-spin mr-1" /> Sending...</>
                                ) : (
                                  <><Send size={12} className="mr-1" /> {chunk.payment_link_token ? 'Resend link' : 'Send link'}</>
                                )}
                              </button>
                            )}
                            <button
                              onClick={() => handleMarkPaid(chunk.installment_number, isOnline ? 'manual' : 'cash')}
                              disabled={actingOn === chunk.installment_number}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {actingOn === chunk.installment_number ? 'Marking...' : `Mark paid (${isOnline ? 'manual' : 'cash'})`}
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div>
                          {chunk.due_date && (
                            <span className="mr-3">
                              <Clock size={11} className="inline mr-1" />
                              Due {chunk.due_date}
                            </span>
                          )}
                          {chunk.paid_at && (
                            <span>
                              Paid {new Date(chunk.paid_at).toLocaleString()}
                              {chunk.payment_method_used && ` · ${chunk.payment_method_used}`}
                            </span>
                          )}
                          {isFirst && chunk.status !== 'paid' && (
                            <span className="text-amber-700">
                              First chunk — usually paid at booking
                            </span>
                          )}
                          {chunk.payment_link_sent_at && chunk.status !== 'paid' && (
                            <span className="ml-3 text-sky-700">
                              <Mail size={11} className="inline mr-1" />
                              Link sent {new Date(chunk.payment_link_sent_at).toLocaleString()}
                              {chunk.payment_link_expires_at && (
                                <> · expires {new Date(chunk.payment_link_expires_at).toLocaleDateString()}</>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      {chunk.notes && (
                        <div className="mt-2 text-xs text-gray-600 italic">{chunk.notes}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Last-link result panel — shows the URL admin just generated
                  so they can copy it (e.g. when SMTP isn't configured and the
                  email didn't actually go out, or when they want to send via
                  SMS / their own channel). */}
              {lastLinkResult && (
                <div className={`mt-4 p-3 rounded border text-xs ${
                  lastLinkResult.email_sent
                    ? 'bg-green-50 border-green-200 text-green-900'
                    : 'bg-amber-50 border-amber-200 text-amber-900'
                }`}>
                  <div className="font-semibold mb-1">
                    Installment #{lastLinkResult.number} — {lastLinkResult.email_sent ? 'Link emailed' : 'Email not sent'}
                  </div>
                  <div className="mb-2">{lastLinkResult.message}</div>
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      readOnly
                      value={lastLinkResult.url}
                      onClick={(e) => e.target.select()}
                      className="flex-1 text-xs font-mono px-2 py-1 border border-gray-300 rounded bg-white text-gray-800"
                    />
                    <button
                      onClick={() => handleCopyLink(lastLinkResult.url)}
                      className="px-2 py-1 bg-gray-700 text-white rounded hover:bg-gray-800 text-xs inline-flex items-center"
                    >
                      <Copy size={11} className="mr-1" /> Copy
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallmentManagementModal;
